import { useState } from 'react';

import { ColumnFiltersState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { closePublishModal, setIsPublishModalOpen } from '@/redux/collections/actions';
import { selectIsPublishModalOpen } from '@/redux/collections/selectors';

import { columns } from '../components/Tables/PublishDocumentsTable/columns';
import { DataTable } from '../components/Tables/PublishDocumentsTable/DataTable';
import { TableSkeleton } from '../components/Tables/TableSkeleton';
import { useAPIDraftDocuments } from '../hooks/useAPIDraftDocuments';
import { useAPIDraftDocumentsCount } from '../hooks/useAPIDraftDocumentsCount';

/**
 * Modal component for publishing documents from a collection
 */
export const PublishModal = () => {
  const { t } = useTranslation();
  const isPublishModalOpen = useSelector(selectIsPublishModalOpen);
  const dispatch = useDispatch();
  const handlePublishModalChange = (open: boolean) => {
    dispatch(setIsPublishModalOpen(open));
  };
  const { collectionId } = useParams();
  const {
    data: documents,
    isLoading,
    publishDocuments,
    isPublishing,
  } = useAPIDraftDocuments({ collection_id: collectionId, enabled: isPublishModalOpen });

  const { count: totalDraftCount, isLoading: isLoadingCount } = useAPIDraftDocumentsCount({
    collection_id: collectionId,
    enabled: isPublishModalOpen,
  });

  // Table related state
  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const closeModal = () => {
    setRowSelection({});
    setColumnFilters([]);
    dispatch(closePublishModal());
  };

  const handleCancel = () => {
    closeModal();
  };

  const handlePublish = () => {
    const selectedDocumentIds = Object.keys(rowSelection);
    publishDocuments({ document_ids: selectedDocumentIds });

    closeModal();
  };

  const handlePublishAll = () => {
    publishDocuments({ include_all: true });
    closeModal();
  };

  const selectedCount = Object.values(rowSelection).filter(Boolean).length;

  return (
    <Dialog open={isPublishModalOpen} onOpenChange={handlePublishModalChange}>
      <DialogContent className="top-[4vh] flex h-[92vh] translate-y-0 flex-col sm:max-w-4xl">
        <DialogHeader className="shrink-0">
          <DialogTitle>{t('collections.publish_drafts')}</DialogTitle>
          <DialogDescription>{t('collections.publish_modal.description')}</DialogDescription>
        </DialogHeader>

        {/* PublishTable with selection functionality */}
        <div className="flex min-h-0 flex-1 flex-col">
          {isLoading ? (
            <TableSkeleton />
          ) : (
            <DataTable
              columns={columns()}
              data={documents || []}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
            />
          )}
        </div>

        <DialogFooter className="shrink-0 flex-row items-center justify-between">
          <Button
            variant="outline-neutral"
            onClick={handlePublishAll}
            disabled={isLoadingCount || totalDraftCount === 0 || isPublishing}
            className="mr-auto"
          >
            {t('collections.publish_all')} {totalDraftCount > 0 && `(${totalDraftCount})`}
          </Button>
          <div className="flex gap-2">
            <DialogClose asChild>
              <Button variant="ghost-neutral" onClick={handleCancel} disabled={isPublishing}>
                {t('common.cancel')}
              </Button>
            </DialogClose>
            <Button onClick={handlePublish} disabled={selectedCount === 0 || isPublishing}>
              {t('collections.publish_documents')} {selectedCount > 0 && `(${selectedCount})`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
